import axios from "axios";
import { getCurrentP1Region } from "../p1Region";
import { readSessionToken } from "../sessionAccessToken";

function isPublicCatalogOptions(value) {
  return (
    !!value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    Object.prototype.hasOwnProperty.call(value, "publicCatalog")
  );
}

/**
 * Public catalog calls (customer map, customer app list, signup location pickers)
 * must not copy sessionStorage selectedPortal onto x-selected-portal.
 * Administrator and Merchant values would reveal test stores.
 */
export const publicCatalogRequest = { publicCatalog: true };

// Retry configuration for FormData uploads
const MAX_RETRIES = 2;
const RETRY_DELAY = 2000; // 2 seconds base delay for production
const RETRY_DELAY_STAGING = 4000; // 4 seconds base delay for staging/localhost

function mapNzPricingWalkthroughEndpoint(endpoint) {
  if (process.env.REACT_APP_NZ_PRICING_WALKTHROUGH !== "1") {
    return endpoint;
  }
  const exact = {
    "v1/merchantStorePricing": "v1/local/walkthrough/merchantStorePricing",
    "v1/merchantStore?includeSignupDisabled=true":
      "v1/local/walkthrough/merchantStore",
    "v1/product?includeAll=true": "v1/local/walkthrough/product",
  };
  if (exact[endpoint]) {
    return exact[endpoint];
  }
  if (endpoint.startsWith("v1/merchantStorePricing/")) {
    return endpoint.replace("v1/", "v1/local/walkthrough/");
  }
  return endpoint;
}

export const api = async function (
  endpoint,
  type,
  data,
  useFormData = false,
  onProgress = null,
  abortSignal = null,
  retryCount = 0,
  options = {}
) {
  endpoint = mapNzPricingWalkthroughEndpoint(endpoint);
  let requestOptions = options && typeof options === "object" ? options : {};
  if (isPublicCatalogOptions(useFormData)) {
    requestOptions = useFormData;
    useFormData = false;
    onProgress = null;
    abortSignal = null;
    retryCount = 0;
  }
  const publicCatalog = requestOptions.publicCatalog === true;

  const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  const hostname = window.location.hostname;
  const isNzRegion = getCurrentP1Region() === "NZ";

  // Determine API domain based on environment
  // Staging branch uses staging API for all non-localhost deployments
  let domain;
  if (isLocalhost) {
    domain = "http://localhost:8095/";
  } else if (isNzRegion) {
    domain = "https://api.priorityonewashclub.co.nz/";
  } else if (
    hostname.includes("staging.dm6wsofv5048b.amplifyapp.com") ||
    hostname.includes("dm6wsofv5048b.amplifyapp.com") ||
    hostname.includes("p1-staging") ||
    !hostname.includes("priorityonewashclub.com") // If it's not the production domain, use staging
  ) {
    // Staging environment - use staging API
    domain = "https://p1-staging-api.priorityonewashclub.com/";
  } else {
    // Production domain - use production API
    domain = "https://api.priorityonewashclub.com/";
  }

  const token = readSessionToken();
  const selectedPortal = sessionStorage.getItem("selectedPortal");

  // Never send "Authorization: Bearer " with a blank token. An empty bearer is
  // not treated as anonymous — Auth0 rejects it as a malformed credential (401).
  // Public callers (signup) omit the header until a real access token exists.
  const headers = {};
  if (token) {
    headers.authorization = `Bearer ${token}`;
  }
  if (useFormData) {
    // Axios appends the multipart boundary when it detects FormData.
    headers["Content-Type"] = "multipart/form-data";
  }

  // Staff catalog loads keep the portal header. Public catalog calls omit it
  // even when an Administrator or Merchant session is stored in this browser.
  if (selectedPortal && !publicCatalog) {
    headers["x-selected-portal"] = selectedPortal;
  }

  // Portal identity: JWT access tokens often omit email, so send the signed-in user.
  if (selectedPortal === "Customer" || selectedPortal === "Merchant") {
    try {
      const currentUserRaw = sessionStorage.getItem("currentUser");
      if (currentUserRaw) {
        const currentUser = JSON.parse(currentUserRaw);
        if (currentUser?.email) {
          headers["x-p1-customer-email"] = String(currentUser.email).toLowerCase().trim();
          headers["x-p1-user-email"] = String(currentUser.email).toLowerCase().trim();
        }
        if (currentUser?._id) {
          headers["x-p1-customer-id"] = String(currentUser._id);
          headers["x-p1-user-id"] = String(currentUser._id);
        }
      }
    } catch (_) {
      // ignore parse errors
    }
    if (!headers["x-p1-customer-email"]) {
      const authEmail = sessionStorage.getItem("authEmail");
      if (authEmail) {
        headers["x-p1-customer-email"] = String(authEmail).toLowerCase().trim();
        headers["x-p1-user-email"] = String(authEmail).toLowerCase().trim();
      }
    }
  }

  // For FormData uploads, set timeout to 5 minutes (300000ms) to handle large files
  // Production may have load balancer timeouts (often 60-120s), but mobile networks are slower
  // Increase timeout for mobile devices to handle slow/spotty mobile connections
  // Staging can use longer timeout since it typically doesn't have LB restrictions
  // Localhost gets even longer timeout to handle slow backend processing
  const isProduction =
    (domain.includes("api.priorityonewashclub.com") && !domain.includes("staging")) ||
    domain.includes("api.priorityonewashclub.co.nz");
  const isDomainLocalhost = domain.includes("localhost") || domain.includes("127.0.0.1");
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const timeout = useFormData
    ? isDomainLocalhost
      ? 600000 // 10 min for localhost (backend may be slow processing photos)
      : isProduction
      ? isMobile
        ? 180000 // 3 min for mobile on production (slower mobile networks need more time)
        : 120000 // 2 min for desktop on production
      : 300000 // 5 min for staging
    : isDomainLocalhost
    ? 60000 // 60s for regular requests on localhost
    : /redemption\/verify|\/xero/i.test(String(endpoint || ""))
    ? 120000 // verify waits on Xero bill create
    : /redemption\/request-otp/i.test(String(endpoint || ""))
    ? 35000 // parallel HOB email + SMS (~20s email channel)
    : 30000; // 30s for regular requests

  // Add timeout warning at 90% of timeout for production
  let timeoutWarning = null;

  try {
    // Set up timeout warning for production FormData uploads
    if (isProduction && useFormData) {
      timeoutWarning = setTimeout(() => {
        console.warn("⚠️ Upload taking longer than expected (90% of timeout reached)");
      }, timeout * 0.9);
    }

    // Create axios request with proper timeout and error handling
    const axiosConfig = {
      method: type,
      url: domain + endpoint,
      headers,
      data: data,
      maxContentLength: 100000000,
      maxBodyLength: 1000000000,
      timeout: timeout,
      // Only add abort signal if explicitly provided (not null/undefined)
      ...(abortSignal ? { signal: abortSignal } : {}),
      // Ensure timeout actually works - validateTimeout option
      validateStatus: function (status) {
        // Don't throw for any status - let us handle it
        return status >= 200 && status < 600;
      },
      // For FormData, ensure Content-Type is set and let axios append boundary
      // We explicitly set "multipart/form-data" in headers, axios will add the boundary
      transformRequest: useFormData
        ? undefined // Don't transform FormData - let axios handle it with boundary
        : undefined,
      // Add onUploadProgress for FormData to track upload progress
      onUploadProgress: useFormData
        ? (progressEvent) => {
            // Log that progress event fired (for debugging)
            console.log("📊 Progress event fired:", {
              loaded: progressEvent.loaded,
              total: progressEvent.total,
              lengthComputable: progressEvent.lengthComputable
            });

            // Always call progress callback even if total is not available yet
            // This ensures we show that upload is happening
            const bytesLoaded = progressEvent.loaded || 0;
            const bytesTotal = progressEvent.total || 0;

            let percentCompleted = 0;
            if (bytesTotal > 0) {
              percentCompleted = Math.round((bytesLoaded * 100) / bytesTotal);
            } else {
              // If total is not available, show indeterminate progress
              percentCompleted = bytesLoaded > 0 ? -1 : 0; // -1 means "uploading but % unknown"
            }

            // Log to console at milestones or every time if total not available
            if (bytesTotal > 0) {
              // Only log at milestones when we have total
              if (percentCompleted % 25 === 0 || percentCompleted === 100) {
                console.log(
                  `📤 Upload progress: ${percentCompleted}% (${(bytesLoaded / 1024 / 1024).toFixed(2)}MB / ${(
                    bytesTotal /
                    1024 /
                    1024
                  ).toFixed(2)}MB)`
                );
              }
            } else {
              // Log every update when total is unknown (for debugging)
              console.log(`📤 Uploading... ${(bytesLoaded / 1024 / 1024).toFixed(2)}MB sent (total unknown)`);
            }

            // Call progress callback if provided (for UI updates)
            // Always call it, even if total is not available, so UI shows upload is happening
            if (onProgress && typeof onProgress === "function") {
              try {
                onProgress({
                  percentCompleted: percentCompleted === -1 ? undefined : percentCompleted,
                  bytesLoaded,
                  bytesTotal,
                  loadedMB: (bytesLoaded / 1024 / 1024).toFixed(2),
                  totalMB: bytesTotal > 0 ? (bytesTotal / 1024 / 1024).toFixed(2) : undefined,
                  isIndeterminate: bytesTotal === 0 && bytesLoaded > 0 // Show indeterminate progress
                });
              } catch (progressError) {
                console.error("❌ Error in progress callback:", progressError);
              }
            } else {
              console.warn("⚠️ Progress callback not provided or not a function");
            }
          }
        : undefined
    };

    // Log request initiation for debugging
    if (useFormData) {
      console.log("🚀 Starting FormData upload request:", {
        url: domain + endpoint,
        method: type,
        hasData: !!data,
        dataType: data?.constructor?.name,
        isFormData: data instanceof FormData,
        timeout: timeout,
        hasOnProgress: typeof axiosConfig.onUploadProgress === "function"
      });
    }

    // Actually send the request - this is where it should start
    console.log("📡 Calling axios() now - request should be sent immediately...");

    let request = await axios(axiosConfig);
    console.log("✅ Axios request completed/resolved with status:", request.status);

    // Only drop a stored token when one was actually sent and rejected.
    // An empty Bearer (Auth0 token not stored yet) must not wipe a token written moments later.
    if (request.status === 401 && token) {
      sessionStorage.removeItem("token");
    }

    // Clear timeout warning if request completed
    if (timeoutWarning) {
      clearTimeout(timeoutWarning);
    }

    if (useFormData) {
      console.log("✅ FormData upload request completed:", {
        status: request.status,
        hasData: !!request.data
      });
    }

    return request;
  } catch (error) {
    // Clear timeout warning on error
    if (timeoutWarning) {
      clearTimeout(timeoutWarning);
    }

    console.error("❌ API Error Details:", {
      domain: domain,
      endpoint: endpoint,
      type: type,
      error: error,
      errorCode: error.code,
      errorMessage: error.message,
      hasResponse: !!error.response,
      hasRequest: !!error.request,
      responseStatus: error.response?.status,
      responseData: error.response?.data
    });

    // Check if it's a timeout error
    const isTimeout = error.code === "ECONNABORTED" || error.message.includes("timeout");
    const isNetworkError = (!error.response && !error.request) || (error.request && !error.response);

    if (isTimeout) {
      console.error("❌ Request timeout:", error.message);
      // Enhance timeout error for production
      if (error.response) {
        error.response.isTimeout = true;
        error.response.timeoutMessage = `Request timed out after ${
          timeout / 1000
        } seconds. The server may be slow or the file too large.`;
      }
    }

    // Check for network errors (common on mobile/production)
    if (!error.response && !error.request) {
      console.error("❌ Network error - no response or request object:", error.message);
    } else if (error.request && !error.response) {
      console.error("❌ Network error - request sent but no response received");
      // This often indicates a timeout or connection issue
      error.response = {
        status: 0,
        statusText: "Network Error",
        data: {
          success: false,
          message: "Network Error",
          details: "Request timed out or connection failed. Please check your internet connection and try again."
        },
        isTimeout: true,
        isNetworkError: true
      };
    }

    // Retry logic for FormData uploads that timeout or fail with network errors
    // Retry for FormData uploads (photo uploads) on all environments
    // Production uses shorter retry delays to avoid long waits for users
    // Staging/localhost can use longer delays since they're typically for development
    // Don't retry if we've already retried MAX_RETRIES times
    if (useFormData && (isTimeout || isNetworkError) && retryCount < MAX_RETRIES) {
      // Production uses shorter retry delays (2s, 4s) to avoid long waits
      // Staging/localhost uses longer delays (4s, 8s) since they're for development
      const baseDelay = isProduction ? RETRY_DELAY : RETRY_DELAY_STAGING;
      const retryDelay = baseDelay * Math.pow(2, retryCount); // Exponential backoff

      console.warn(`⚠️ Upload failed, retrying in ${retryDelay / 1000}s... (attempt ${retryCount + 1}/${MAX_RETRIES})`);

      // Wait before retrying
      await new Promise((resolve) => setTimeout(resolve, retryDelay));

      // Retry the request with incremented retry count
      return api(
        endpoint,
        type,
        data,
        useFormData,
        onProgress,
        abortSignal,
        retryCount + 1,
        requestOptions
      );
    }

    // console.log('API response: ', error.response);
    return error.response;
  }
};
